import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createStorePerformanceRequest, StorePerformanceInterval } from '../../../../../constants';
import {
  BudgetPlannedData,
  ReceiptSalesTimeResponse,
  useAsyncAction,
  useTransaction,
  useUser,
} from '../../../../../hooks';
import { CurrencyFormatterOptions, useCurrencyTools } from '../../../../../hooks/useCurrencyTools';
import { Container, Icon, Icons, Typography } from '../../../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../../../stories/atoms/containerV2/ContainerV2';
import { DropdownMenu } from '../../../../../stories/molecules';
import { ContainerDrawer } from '../../../../../stories/molecules/container-drawer/ContainerDrawer';
import { muiTheme } from '../../../../../theme';
import { homeTranslations } from '../../../../../translations';
import { formatAmountWithSeparator, isFailureResponse } from '../../../../../utils';
import { calculateTrend } from '../../../../../utils/budgetPlanned';
import {
  BudgetPlannedContent,
  getBudgetAndPlannedPermissions,
} from '../../../common/budgetPlannedContent/BudgetPlannedContent';
import { InfoTooltipIcon } from '../../../common/infoTooltipIcon/InfoTooltipIcon';
import { StyledTrendIconComponent } from '../../../common/styledTrendIcon/StyledTrendIcon';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { StyledIcon, StyledTypography } from './EfficiencyWidget.styles';

type BudgetPlannedSliderType = 'Takt1Slider' | 'SptSlider' | 'EffectiveHoursSlider';
const budgetPlannedSliderTypes: BudgetPlannedSliderType[] = ['Takt1Slider', 'SptSlider', 'EffectiveHoursSlider'];

const { common, efficiencyWidget: efficiencyWidgetTranslations, budgetPlanned, salesNet } = homeTranslations;

type EfficiencyWidgetProps = {
  isToday: boolean;
  currentDate: Date;
  interval: StorePerformanceInterval;
};

type MetricSectionProps = {
  metricName: string;
  icon: Icons;
  actual: number;
  lastWeek: number;
  lastYear: number;
  getFormattedCurrency: (value: number, options?: CurrencyFormatterOptions) => string;
  isEffectiveHours?: boolean;
  isToday?: boolean;
};

type EfficiencyMetricNames = 'takt1' | 'spt' | 'effectiveHours';

const EfficiencyMetricsSection: FC<MetricSectionProps> = ({
  metricName,
  icon,
  actual,
  lastWeek,
  lastYear,
  getFormattedCurrency,
  isToday = false,
  isEffectiveHours = false,
}) => {
  const { t } = useTranslation();
  const isMobileView = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const isTakt1 = metricName === 'takt1';

  const getTrendTooltipText = (period: 'lastWeek' | 'lastYear') => {
    const { tooltips } = efficiencyWidgetTranslations;

    let tooltipTranslation;

    if (metricName === 'takt1') {
      tooltipTranslation = period === 'lastWeek' ? tooltips.lastWeekTakt1 : tooltips.lastYearTakt1;
    } else if (metricName === 'spt') {
      tooltipTranslation = period === 'lastWeek' ? tooltips.lastWeekSPT : tooltips.lastYearSPT;
    } else {
      tooltipTranslation =
        period === 'lastWeek' ? tooltips.lastWeekEffectiveHours : tooltips.lastYearEffectiveHours;
    }

    return t(tooltipTranslation.key, tooltipTranslation.defaultValue, {
      changeNote: isToday ? '' : t(common.changeVsCurrentNote.key, common.changeVsCurrentNote.defaultValue),
    });
  };

  return (
    <ColumnFlexContainer flex={1} padding={[2]} hasBorder borderRadius={[2]} data-testid={`${metricName}-card`}>
      <ContainerV2 display="flex" flexDirection={isMobileView ? 'row' : 'column'} justifyContent="between">
        <RowFlexContainer alignItems="center">
          <StyledIcon icon={icon} />
          <Typography type="button">
            {t(
              efficiencyWidgetTranslations[metricName as EfficiencyMetricNames].title.key,
              efficiencyWidgetTranslations[metricName as EfficiencyMetricNames].title.defaultValue,
            )}{' '}
          </Typography>
          {isEffectiveHours && (
            <Typography type="caption">
              (
              {isToday
                ? t(budgetPlanned.planned.key, budgetPlanned.planned.defaultValue)
                : t(salesNet.actual.key, salesNet.actual.defaultValue)}
              )
            </Typography>
          )}
          {isMobileView && (
            <InfoTooltipIcon
              position="bottom-start"
              tooltipText={t(
                efficiencyWidgetTranslations[metricName as EfficiencyMetricNames].text.key,
                efficiencyWidgetTranslations[metricName as EfficiencyMetricNames].text.defaultValue,
              )}
              testId="header-icon"
            />
          )}
        </RowFlexContainer>

        <Typography type={isMobileView ? 'body2' : 'h4'}>
          {isTakt1 ? getFormattedCurrency(actual, { showCurrencyCode: false }) : formatAmountWithSeparator(actual)}
        </Typography>
      </ContainerV2>
      {!isMobileView && (
        <StyledTypography>
          {t(
            efficiencyWidgetTranslations[metricName as EfficiencyMetricNames].text.key,
            efficiencyWidgetTranslations[metricName as EfficiencyMetricNames].text.defaultValue,
          )}
        </StyledTypography>
      )}
      <ContainerV2
        data-testid="last-week-container"
        display="flex"
        flexDirection={isMobileView ? 'row' : 'column'}
        justifyContent="between"
      >
        <RowFlexContainer alignItems="center" margin={[2, 0, 1]}>
          <Typography type="caption" color="textGray">
            {t(common.lastWeek.key, common.lastWeek.defaultValue)}
          </Typography>
          <InfoTooltipIcon
            tooltipText={getTrendTooltipText('lastWeek')}
            testId={`${metricName}-last-week-info-icon`}
          />
        </RowFlexContainer>
        <div>
          <StyledTrendIconComponent
            originalAmount={actual}
            previousAmount={isEffectiveHours ? (lastWeek < 1 ? 0 : lastWeek) : lastWeek}
            direction="horizontal"
            valueFormatter={(value) =>
              isTakt1 ? getFormattedCurrency(value, { showCurrencyCode: false }) : formatAmountWithSeparator(value)
            }
            textOptions={{ type: isMobileView ? 'body2' : 'button' }}
            calculateCustomTrend={(originalAmount, previousAmount) =>
              calculateTrend(originalAmount, previousAmount, isEffectiveHours)
            }
            hideTrendText={isToday}
            fullWidth
          />
        </div>
      </ContainerV2>
      <ContainerV2
        data-testid="last-year-container"
        display="flex"
        flexDirection={isMobileView ? 'row' : 'column'}
        justifyContent="between"
      >
        <RowFlexContainer alignItems="center" margin={[2, 0, 1]}>
          <Typography type="caption" color="textGray">
            {t(common.lastYear.key, common.lastYear.defaultValue)}
          </Typography>
          <InfoTooltipIcon
            tooltipText={getTrendTooltipText('lastYear')}
            testId={`${metricName}-last-year-info-icon`}
          />
        </RowFlexContainer>
        <div>
          <StyledTrendIconComponent
            originalAmount={actual}
            previousAmount={isEffectiveHours ? (lastYear < 1 ? 0 : lastYear) : lastYear}
            direction="horizontal"
            valueFormatter={(value) =>
              isTakt1 ? getFormattedCurrency(value, { showCurrencyCode: false }) : formatAmountWithSeparator(value)
            }
            textOptions={{ type: isMobileView ? 'body2' : 'button' }}
            calculateCustomTrend={(originalAmount, previousAmount) =>
              calculateTrend(originalAmount, previousAmount, isEffectiveHours)
            }
            hideTrendText={isToday}
            fullWidth
          />
        </div>
      </ContainerV2>
    </ColumnFlexContainer>
  );
};

export const EfficiencyWidget: FC<EfficiencyWidgetProps> = observer(({ isToday, currentDate, interval }) => {
  const { t } = useTranslation();
  const {
    get: { userPermissions },
  } = useUser();
  const { getFormattedCurrency } = useCurrencyTools(interval, currentDate);
  const isMobileView = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const [isBudgetSliderOpen, setIsBudgetSliderOpen] = useState(false);
  const [selectedSlider, setSelectedSlider] = useState<BudgetPlannedSliderType>('Takt1Slider');

  const { canViewBudget, canViewPlanned, showBudgetAndPlanned } = getBudgetAndPlannedPermissions(userPermissions);

  const { fetchReceiptSalesAnalysisData, fetchEffectiveHoursBudget, fetchTakt1Budget, fetchSptBudget } =
    useTransaction();

  const {
    result: receiptSalesAnalysisData,
    isLoading: receiptSalesAnalysisLoading,
    isError: receiptSalesAnalysisError,
    triggerAction: triggerFetchReceiptSalesAnalysis,
  } = useAsyncAction<ReceiptSalesTimeResponse>();

  // Trigger Budget and Planned data
  const {
    result: effectiveHoursBudgetPlanned,
    isLoading: effectiveHoursBudgetPlannedLoading,
    isError: effectiveHoursBudgetPlannedError,
    triggerAction: triggerFetchEffectiveHoursBudgetPlanned,
  } = useAsyncAction<BudgetPlannedData>();

  const {
    result: takt1BudgetPlanned,
    isLoading: takt1BudgetPlannedLoading,
    isError: takt1BudgetPlannedError,
    triggerAction: triggerFetchTakt1BBudgetPlanned,
  } = useAsyncAction<BudgetPlannedData>();

  const {
    result: sptBudgetPlanned,
    isLoading: sptBudgetPlannedLoading,
    isError: sptBudgetPlannedError,
    triggerAction: triggerFetchSptBudgetPlanned,
  } = useAsyncAction<BudgetPlannedData>();

  const fetchReceiptSalesAnalysis = async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });
    const response = await fetchReceiptSalesAnalysisData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  const fetchBudgetPlanned = async (budgetType: EfficiencyMetricNames) => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });

    let response;

    if (budgetType === 'takt1') {
      response = await fetchTakt1Budget(request);
    } else if (budgetType === 'spt') {
      response = await fetchSptBudget(request);
    } else {
      response = await fetchEffectiveHoursBudget(request);
    }

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  const fetchWidgetData = () => {
    triggerFetchReceiptSalesAnalysis(fetchReceiptSalesAnalysis);
    triggerFetchTakt1BBudgetPlanned(() => fetchBudgetPlanned('takt1'));
    triggerFetchSptBudgetPlanned(() => fetchBudgetPlanned('spt'));
    triggerFetchEffectiveHoursBudgetPlanned(() => fetchBudgetPlanned('effectiveHours'));
  };

  useEffect(() => {
    fetchWidgetData();
    setIsBudgetSliderOpen(false);
  }, [currentDate]);

  const currentEffectiveHours = isToday
    ? effectiveHoursBudgetPlanned?.planned ?? 0
    : receiptSalesAnalysisData?.effectiveHours?.today ?? 0;

  const defaultNoData =
    !!receiptSalesAnalysisData &&
    receiptSalesAnalysisData.takt1.today === 0 &&
    receiptSalesAnalysisData.spt.today === 0;

  const isEffectiveHoursZero =
    (currentEffectiveHours === 0 || currentEffectiveHours < 1) &&
    receiptSalesAnalysisData?.takt1.today !== 0 &&
    receiptSalesAnalysisData?.spt.today !== 0;

  return (
    <ContainerDrawer
      isOpen={isBudgetSliderOpen}
      onClose={() => setIsBudgetSliderOpen(false)}
      sliderContent={
        <>
          {selectedSlider === 'Takt1Slider' && takt1BudgetPlanned && (
            <BudgetPlannedContent
              title={t(
                efficiencyWidgetTranslations.takt1.title.key,
                efficiencyWidgetTranslations.takt1.title.defaultValue,
              )}
              currentData={receiptSalesAnalysisData?.takt1.today ?? null}
              isLoading={takt1BudgetPlannedLoading}
              isError={takt1BudgetPlannedError}
              canViewBudget={canViewBudget}
              canViewPlanned={canViewPlanned}
              budgetPlanned={takt1BudgetPlanned}
              budgetNoData={takt1BudgetPlanned.budget === 0}
              plannedNoData={takt1BudgetPlanned.planned === 0}
              isCurrency
            />
          )}

          {selectedSlider === 'SptSlider' && sptBudgetPlanned && (
            <BudgetPlannedContent
              title={t(
                efficiencyWidgetTranslations.spt.title.key,
                efficiencyWidgetTranslations.spt.title.defaultValue,
              )}
              currentData={receiptSalesAnalysisData?.spt.today ?? null}
              isLoading={sptBudgetPlannedLoading}
              isError={sptBudgetPlannedError}
              canViewBudget={canViewBudget}
              canViewPlanned={canViewPlanned}
              budgetPlanned={sptBudgetPlanned}
              budgetNoData={sptBudgetPlanned.budget === 0}
              plannedNoData={sptBudgetPlanned.planned === 0}
            />
          )}

          {selectedSlider === 'EffectiveHoursSlider' && effectiveHoursBudgetPlanned && (
            <BudgetPlannedContent
              title={t(
                efficiencyWidgetTranslations.effectiveHours.title.key,
                efficiencyWidgetTranslations.effectiveHours.title.defaultValue,
              )}
              currentData={receiptSalesAnalysisData?.effectiveHours?.today ?? null}
              isLoading={effectiveHoursBudgetPlannedLoading}
              isError={effectiveHoursBudgetPlannedError}
              canViewBudget={canViewBudget}
              canViewPlanned={canViewPlanned}
              budgetPlanned={effectiveHoursBudgetPlanned}
              budgetNoData={effectiveHoursBudgetPlanned.budget === 0}
              plannedNoData={effectiveHoursBudgetPlanned.planned === 0}
              isEffectiveHours
            />
          )}
        </>
      }
      width={isMobileView ? '250px' : '300px'}
    >
      <WidgetCardContent
        headerIcon={Icons.SALESEFFICIENCY}
        headerText={t(efficiencyWidgetTranslations.title.key, efficiencyWidgetTranslations.title.defaultValue)}
        subHeaderText={t(
          efficiencyWidgetTranslations.subTitle.key,
          efficiencyWidgetTranslations.subTitle.defaultValue,
        )}
        toolTipText={t(
          efficiencyWidgetTranslations.tooltip.key,
          efficiencyWidgetTranslations.tooltip.defaultValue,
        )}
        lastUpdatedDate={receiptSalesAnalysisData?.lastUpdated} // TODO: change this to single last updated when BE creates new endpoint for effciency. [BTCOBUI-2751]
        isLoading={receiptSalesAnalysisLoading}
        isError={receiptSalesAnalysisError}
        errorText={t(efficiencyWidgetTranslations.error.key, efficiencyWidgetTranslations.error.defaultValue)}
        errorRetryHandler={fetchWidgetData}
        isNoData={defaultNoData || isEffectiveHoursZero}
        noDataText={
          isEffectiveHoursZero
            ? t(efficiencyWidgetTranslations.noData.key, efficiencyWidgetTranslations.noData.defaultValue)
            : undefined
        }
        headerSideContent={
          <RowFlexContainer alignItems="center">
            <DropdownMenu
              data-testid="budget-planned-dropdown"
              dropdownLabel={''}
              buttonContentProps={{
                iconOptions: { customIcon: <Icon icon={Icons.MORE} /> },
              }}
              menuItems={[
                {
                  key: budgetPlannedSliderTypes[0],
                  disabled: !showBudgetAndPlanned || takt1BudgetPlannedError,
                  hasTooltip: !showBudgetAndPlanned,
                  tooltipText: (
                    <Container padding={[1]}>
                      <Typography color="white">
                        {t(
                          budgetPlanned.noPermissionBudgetTooltip.key,
                          budgetPlanned.noPermissionBudgetTooltip.defaultValue,
                        )}
                      </Typography>
                    </Container>
                  ),
                  item: (
                    <Typography margin={[0, 2, 0]}>
                      {t(
                        efficiencyWidgetTranslations.takt1.budget.key,
                        efficiencyWidgetTranslations.takt1.budget.defaultValue,
                      )}
                    </Typography>
                  ),
                },
                {
                  key: budgetPlannedSliderTypes[1],
                  disabled: !showBudgetAndPlanned || sptBudgetPlannedError,
                  hasTooltip: !showBudgetAndPlanned,
                  tooltipText: (
                    <Container padding={[1]}>
                      <Typography color="white">
                        {t(
                          budgetPlanned.noPermissionBudgetTooltip.key,
                          budgetPlanned.noPermissionBudgetTooltip.defaultValue,
                        )}
                      </Typography>
                    </Container>
                  ),
                  item: (
                    <Typography margin={[0, 2, 0]}>
                      {t(
                        efficiencyWidgetTranslations.spt.budget.key,
                        efficiencyWidgetTranslations.spt.budget.defaultValue,
                      )}
                    </Typography>
                  ),
                },
                {
                  key: budgetPlannedSliderTypes[2],
                  disabled: !showBudgetAndPlanned || effectiveHoursBudgetPlannedError,
                  hasTooltip: !showBudgetAndPlanned,
                  tooltipText: (
                    <Container padding={[1]}>
                      <Typography color="white">
                        {t(
                          budgetPlanned.noPermissionBudgetTooltip.key,
                          budgetPlanned.noPermissionBudgetTooltip.defaultValue,
                        )}
                      </Typography>
                    </Container>
                  ),
                  item: (
                    <Typography margin={[0, 2, 0]}>
                      {t(
                        efficiencyWidgetTranslations.effectiveHours.budget.key,
                        efficiencyWidgetTranslations.effectiveHours.budget.defaultValue,
                      )}
                    </Typography>
                  ),
                },
              ]}
              minWidth={'0px'}
              isCompact
              initialSelected={[]}
              onSelect={(selectedItems) => {
                setSelectedSlider(selectedItems[0] as BudgetPlannedSliderType);
                setIsBudgetSliderOpen(true);
              }}
              isOnlyIcon
              allowTooltip
            />
          </RowFlexContainer>
        }
        content={
          <>
            <ContainerV2
              display="flex"
              flexDirection={isMobileView ? 'column' : 'row'}
              flex={1}
              padding={[4]}
              gap={[2]}
              justifyContent="between"
              data-testid="efficiency-widget-wrapper"
            >
              <EfficiencyMetricsSection
                key="takt1"
                metricName="takt1"
                icon={Icons.DOLLAR}
                actual={receiptSalesAnalysisData?.takt1.today || 0}
                lastWeek={receiptSalesAnalysisData?.takt1.lastWeek || 0}
                lastYear={receiptSalesAnalysisData?.takt1.lastYear || 0}
                isToday={isToday}
                getFormattedCurrency={getFormattedCurrency}
              />
              <EfficiencyMetricsSection
                key="spt"
                metricName="spt"
                icon={Icons.BAG}
                actual={receiptSalesAnalysisData?.spt.today || 0}
                lastWeek={receiptSalesAnalysisData?.spt.lastWeek || 0}
                lastYear={receiptSalesAnalysisData?.spt.lastYear || 0}
                isToday={isToday}
                getFormattedCurrency={getFormattedCurrency}
              />
              <EfficiencyMetricsSection
                key="effectiveHours"
                metricName="effectiveHours"
                icon={Icons.CLOCK}
                actual={currentEffectiveHours || 0}
                lastWeek={receiptSalesAnalysisData?.effectiveHours?.lastWeek || 0}
                lastYear={receiptSalesAnalysisData?.effectiveHours?.lastYear || 0}
                isEffectiveHours
                isToday={isToday}
                getFormattedCurrency={getFormattedCurrency}
              />
            </ContainerV2>
          </>
        }
      />
    </ContainerDrawer>
  );
});
